import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { ContractModificationTypes, IElecContract, IFetchContractsResponse } from '../types/contracts';
import { IResPartnerAddress, UseAddressState } from '../types/address';
import { Address } from '@/components/form-fields/AddressForm/types/address';
import { VirtualBatteryJoinTypes } from '@/components/VirtualBattery/types/virtualBattery';
import { UseRefCatastralState } from '../types/refCatastral';
import { UseInvoiceOnlineState, InvoiceOnlineTypes } from '../types/invoiceOnline';
import { NoSIPSCessionStates } from '../types/noSIPSCession';
import { CouponDetail, CouponsState } from '@/components/form-fields/CouponField/types/coupons';

export interface IElecContractsState {
  status: 'uninitialized' | 'loading' | 'succeeded' | 'failed';
  downloading_contract: boolean;
  filterContract?: string;
  view_contract?: IElecContract["id"];
  items: IElecContract[],
  total_items: number;
  offset_items: number;
  page_items: number;
  addressess: {
    available: IResPartnerAddress[];
    status: Record<IElecContract["id"], {
      error_code?: string;
      status: UseAddressState;
    }>
  };
  refCatastral: {
    status: Record<IElecContract["id"], {
      error_code?: string;
      status: UseRefCatastralState;

    }>
  };
  invoiceOnline: {
    status: Record<IElecContract["id"], {
      error_code?: string;
      status: UseInvoiceOnlineState;
    }>
  },
  coupons: {
    status: Record<IElecContract["id"], {
      error_code?: string;
      status: CouponsState;
    }>
  };
}

const initialState: IElecContractsState = {
  status: 'uninitialized',
  downloading_contract: false,
  items: [],
  total_items: 0,
  offset_items: 0,
  page_items: 0,
  addressess: {
    available: [],
    status: {}
  },
  refCatastral: {
    status: {}
  },
  invoiceOnline: {
    status: {}
  },
  coupons: {
    status: {}
  }
};

export const elecContractsSlice = createSlice({
  name: 'electricity',
  initialState,
  reducers: {
    receivedContracts: (state, action: PayloadAction<IFetchContractsResponse<IElecContract>>) => {
      state.total_items = action.payload.n_items;
      state.offset_items = action.payload.offset;
      state.page_items = action.payload.limit;
      state.items = action.payload.items;
      state.status = 'succeeded';
    },
    contractUpdated: (state, action: PayloadAction<IElecContract>) => {
      const toUpdateIdx = state.items.findIndex(contract => contract.id === action.payload.id);
      if (toUpdateIdx !== -1) {
        state.items[toUpdateIdx] = action.payload;
      }
    },
    fetchContractsRequested: state => {
      state.status = 'loading';
    },
    filterContractsRequested: (state, action: PayloadAction<IElecContractsState["filterContract"]>) => {
      state.filterContract = action.payload;
    },
    filterContractsCleared: state => {
      state.filterContract = undefined;
    },
    exportContractRequested: state => {
      state.downloading_contract = true;
    },
    exportContractDone: state => {
      state.downloading_contract = false;
    },
    viewContractSet: (state, action: PayloadAction<IElecContractsState["view_contract"]>) => {
      state.view_contract = action.payload;
    },
    phoneSet: (state, action: PayloadAction<IElecContract["notification_address"]["phone"]>) => {
      if (state.view_contract) {
        state.items.find(
          c => c.id === state.view_contract
        )!.notification_address.phone = action.payload;
      }
    },
    notificationPhoneSet: (state, action: PayloadAction<IElecContract["notification_address"]["phone"]>) => {
      if (state.view_contract) {
        state.items.find(
          c => c.id === state.view_contract
        )!.notification_address["phone"] = action.payload;
      }
    },
    notificationEmailSet: (state, action: PayloadAction<IElecContract["notification_address"]["email"]>) => {
      if (state.view_contract) {
        state.items.find(
          c => c.id === state.view_contract
        )!.notification_address["email"] = action.payload;
      }
    },
    signatureStarted: (state, action: PayloadAction<ContractModificationTypes>) => {
      const contract = state.items.find(c => c.id === state.view_contract)!

      if (!contract.waitingForSignature) {
        contract.waitingForSignature = [];
      }

      contract.waitingForSignature.push(action.payload);
    },
    recievedAddress: (state, action: PayloadAction<IElecContractsState["addressess"]["available"]>) => {
      state.addressess.available = action.payload;
      state.addressess.status[state.view_contract!].status = "selecting";
    },
    notificationFormOpened: (state) => {
      state.addressess.status[state.view_contract!] = {status: "uninitialized"}
    },
    notificationFormFailed: (state) => {
      state.addressess.status[state.view_contract!] = {status: "error"}
    },
    notificationFormSubmited: (state) => {
      state.addressess.status[state.view_contract!] = {status: "submitting"}
    },
    notificationFormClosed: (state) => {
      state.addressess.status[state.view_contract!] = {status: "uninitialized"}
    },
    addressFetching: (state) => {
      state.addressess.status[state.view_contract!] = {status: "fetching"}
    },
    notificationAddressUpdated: (state, action: PayloadAction<number>) => {
      if (state.view_contract) {
        // Buscar l'adreça disponible que s'ha seleccionat
        const selectedAddress = state.addressess.available.find(
          address => address.id === action.payload
        )

        // Assignar el IResPartnerAddress al notification_address
        if (selectedAddress){
          state.items.find(
            c => c.id === state.view_contract
          )!.notification_address = selectedAddress;
        }
      }

      state.addressess.status[state.view_contract!].status = "success";
    },
    notificationAddressCreated: (state, action: PayloadAction<IResPartnerAddress>) => {
      state.addressess.available.push(action.payload)
      state.items.find(
        c => c.id === state.view_contract
      )!.notification_address = action.payload;

      state.addressess.status[state.view_contract!].status = "success";
    },
    notificationAddressModified: (state, action: PayloadAction<IResPartnerAddress>) => {
      const addressIndex = state.addressess.available.findIndex(
        address => address.id === action.payload.id
      );
      if (addressIndex !== -1) {
        state.addressess.available[addressIndex] = action.payload;
      }
      state.items.find(
        c => c.id === state.view_contract
      )!.notification_address = action.payload;
      state.addressess.status[state.view_contract!].status = "success";
    },
    virtualBatteryTypeUpdated: (state, action: PayloadAction<{selectedValue: VirtualBatteryJoinTypes, polissaBateriaVirtualId: number}>) => {
      // TODO: Loading status ?
       if (state.view_contract !== -1 ) {
        const contractIndex = state.items.findIndex(
          c => c.id === state.view_contract
        )
        if (contractIndex !== -1 ) {
          const batIndex = state.items[contractIndex].bateria_virtual!.findIndex(
            bat => bat.id === action.payload.polissaBateriaVirtualId
          )
          if (batIndex !== -1) state.items[contractIndex].bateria_virtual![batIndex].gestio_descomptes = action.payload.selectedValue;
        }
      }
    },
    refCatastralChanged: (state, action: PayloadAction<string>) => {
      if (state.view_contract) {
        const contract = state.items.find(
          c => c.id === state.view_contract
        )!.ref_catastral = action.payload;
      }
    },
    refCatastralFormOpened: (state) => {
      state.refCatastral.status[state.view_contract!] = {status: "uninitialized"}
    },
    refCatastralFormFailed: (state) => {
      state.refCatastral.status[state.view_contract!] = {status: "error"}
    },
    refCatastralFormSubmited: (state) => {
      state.refCatastral.status[state.view_contract!] = {status: "submitting"}
    },
    refCatastralFormSuccessed: (state) => {
      state.refCatastral.status[state.view_contract!] = {status: "success"}
    },
    refCatastralFormClosed: (state) => {
      state.refCatastral.status[state.view_contract!] = {status: "uninitialized"}
    },
    invoiceOnlineChanged: (state, action: PayloadAction<InvoiceOnlineTypes>) => {
      if (state.view_contract) {
        const contract = state.items.find(
          c => c.id === state.view_contract
        )!.invoice_online = action.payload;
      }
    },
    invoiceOnlineFormOpened: (state) => {
      state.invoiceOnline.status[state.view_contract!] = {status: "uninitialized"}
    },
    invoiceOnlineFormFailed: (state) => {
      state.invoiceOnline.status[state.view_contract!] = {status: "error"}
    },
    invoiceOnlineFormSubmited: (state) => {
      state.invoiceOnline.status[state.view_contract!] = {status: "submitting"}
    },
    invoiceOnlineFormSuccessed: (state) => {
      state.invoiceOnline.status[state.view_contract!] = {status: "success"}
    },
    invoiceOnlineFormClosed: (state) => {
      state.invoiceOnline.status[state.view_contract!] = {status: "uninitialized"}
    },
    changeIbanSuccessed: (state) => {
      if (state.view_contract) {
        const contract = state.items.find(
          c => c.id === state.view_contract
        )!.bank_account.active_signature_proces = true;
      }
    },
    setNoSIPSCessionState: (state, action: PayloadAction<NoSIPSCessionStates>) => {
      if (state.view_contract !== -1 ) {
        const contractIndex = state.items.findIndex(
          c => c.id === state.view_contract
        )
        if (contractIndex != -1 ) {
          state.items[contractIndex].no_cession_sips.state = action.payload;
        }
      }
    },
    setNoSIPSCessionIsWaitingForSignature: (state, action: PayloadAction<boolean>) => {
      if (state.view_contract !== -1 ) {
        const contractIndex = state.items.findIndex(
          c => c.id === state.view_contract
        )
        if (contractIndex != -1 ) {
          state.items[contractIndex].no_cession_sips.waiting_for_signature = action.payload;
        }
      }
    },
    couponFormOpened: (state) => {
      state.coupons.status[state.view_contract!] = {status: "idle"}
    },
    couponFormFailed: (state) => {
      state.coupons.status[state.view_contract!] = {status: "error"}
    },
    couponFormSubmited: (state) => {
      state.coupons.status[state.view_contract!] = {status: "submitting"}
    },
    couponFormSuccessed: (state, action: PayloadAction<Array<CouponDetail>>) => {
      state.coupons.status[state.view_contract!] = {status: "success"}
      if (state.view_contract !== -1 ) {
        const contractIndex = state.items.findIndex(
          c => c.id === state.view_contract
        )
        if (contractIndex !== -1 ) {
          state.items[contractIndex].promocodes = action.payload;
        }
      }
    },
    couponFormClosed: (state) => {
      state.coupons.status[state.view_contract!] = {status: "idle"}
    },
  }
})

export const actions = elecContractsSlice.actions;

export default elecContractsSlice.reducer;
